import { html } from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'

import addKlarnaLogo, {
  addKlarnaIfNotAvailable,
  replaceLogoTextWithHTMLNode,
} from '../addKlarnaLogo/addKlarnaLogo'

function renderButton(
  ctaLabel: string,
  onLearnMoreClick: () => void,
  learnMoreClickAction: 'popup' | 'iframe'
) {
  const ariaLabelText = ctaLabel || ''

  return html`
    <button
      class="link"
      part="osm-cta"
      aria-label="${ariaLabelText} ${learnMoreClickAction === 'popup'
        ? '(Opens in a new window or tab)'
        : '(Opens in the current tab)'}"
      @click=${onLearnMoreClick}
    >
      ${unsafeHTML(ariaLabelText)}
    </button>
  `
}

function renderContent(
  text: string,
  ctaLabel: string,
  onLearnMoreClick: () => void,
  klarnaLogoAction: 'STYLE_LOGO_BOLD' | 'DEFAULT' = 'STYLE_LOGO_BOLD',
  learnMoreClickAction: 'popup' | 'iframe'
) {
  return html`
    <p part="osm-message" class="text osm-message">
      ${klarnaLogoAction === 'DEFAULT'
        ? html`${unsafeHTML(text)}`
        : addKlarnaLogo(addKlarnaIfNotAvailable(`${text}`))}
      ${renderButton(ctaLabel, onLearnMoreClick, learnMoreClickAction)}
    </p>
  `
}

export function renderPlainMarkdownForCheckout(
  markdownContent: string,
  onLearnMoreClick: () => void,
  klarnaLogoAction: 'STYLE_LOGO_BOLD' | 'DEFAULT' = 'STYLE_LOGO_BOLD'
) {
  if (!markdownContent) return null
  let cleanedMarkdownContent = markdownContent.replace(/<\/?p[^>]*>/g, '')
  if (!cleanedMarkdownContent) return null

  cleanedMarkdownContent = cleanedMarkdownContent.replace(
    /<a href="([^"]+)" rel="noopener noreferrer" target="_blank">/g,
    `<a id="checkout-cta-link" class="checkout-cta-link" href="$1" target="_blank" role="button">`
  )

  const messageContent =
    klarnaLogoAction === 'DEFAULT'
      ? cleanedMarkdownContent
      : replaceLogoTextWithHTMLNode(addKlarnaIfNotAvailable(cleanedMarkdownContent))

  return html` ${klarnaLogoAction === 'DEFAULT'
    ? html`${unsafeHTML(messageContent)}`
    : addKlarnaLogo(addKlarnaIfNotAvailable(`${messageContent}`))}`
}

export function renderMarkdownWithButton(
  markdownContent: string,
  ctaLabel: string,
  onLearnMoreClick: () => void,
  klarnaLogoAction: 'STYLE_LOGO_BOLD' | 'DEFAULT' = 'STYLE_LOGO_BOLD',
  learnMoreClickAction: 'popup' | 'iframe'
) {
  if (!markdownContent) return null
  const cleanedMarkdownContent = markdownContent.replace(/<\/?p[^>]*>/g, '')
  if (!cleanedMarkdownContent) return null

  const messageContent =
    klarnaLogoAction === 'DEFAULT'
      ? cleanedMarkdownContent
      : replaceLogoTextWithHTMLNode(addKlarnaIfNotAvailable(cleanedMarkdownContent))

  const linkMatch = messageContent.match(/<a [^>]*>(.*?)<\/a>/)

  if (!linkMatch) {
    return renderContent(
      messageContent,
      ctaLabel,
      onLearnMoreClick,
      klarnaLogoAction,
      learnMoreClickAction
    )
  }

  const [leftText, rightText] = messageContent.split(linkMatch[0])
  const linkText = linkMatch[1]

  return html`
    <p part="osm-message" class="text osm-message">
      ${unsafeHTML(leftText)}${renderButton(
        linkText,
        onLearnMoreClick,
        learnMoreClickAction
      )}${unsafeHTML(rightText)}
      ${renderButton(ctaLabel, onLearnMoreClick, learnMoreClickAction)}
    </p>
  `
}

export function renderDefaultHTMLContent(
  mainText: string,
  ctaLabel: string,
  onLearnMoreClick: () => void,
  klarnaLogoAction: 'STYLE_LOGO_BOLD' | 'DEFAULT' = 'STYLE_LOGO_BOLD',
  learnMoreClickAction: 'popup' | 'iframe'
) {
  return renderContent(mainText, ctaLabel, onLearnMoreClick, klarnaLogoAction, learnMoreClickAction)
}
